// src/components/UserSubscription.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, Navigate } from 'react-router-dom';
import { BiCrown } from 'react-icons/bi';
import { doc, getDoc, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import LoadingSpinner from './LoadingSpinner';

// Enhanced date formatting function that handles all timestamp formats
const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  // Create a date object from different possible timestamp formats
  let dateObj;
  
  // Handle Firestore timestamps (with seconds and nanoseconds)
  if (timestamp.seconds !== undefined && timestamp.nanoseconds !== undefined) {
    dateObj = new Date(timestamp.seconds * 1000);
  } 
  // Handle Firestore timestamps (with toDate method)
  else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    dateObj = timestamp.toDate();
  } 
  // Handle JavaScript Date objects
  else if (timestamp instanceof Date) {
    dateObj = timestamp;
  } 
  // Handle Unix timestamp in seconds
  else if (typeof timestamp === 'number') {
    dateObj = new Date(timestamp * 1000);
  } 
  // Handle ISO date strings
  else if (typeof timestamp === 'string') {
    dateObj = new Date(timestamp);
  } 
  else {
    return 'N/A';
  }
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) return 'N/A';
  
  // Format to "Sep 23, 2025" 
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const UserSubscription = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const userId = user.uid;
        
        // First try: Check purchases collection using userId
        const purchaseRef = doc(db, 'purchases', userId);
        const purchaseDoc = await getDoc(purchaseRef);

        if (purchaseDoc.exists()) {
          console.log('Found subscription in purchases collection', purchaseDoc.data());
          setSubscription(purchaseDoc.data());
          setLoading(false);
          return;
        }
        
        // Second try: Check users collection using userId
        const userDocRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists() && (userDoc.data().subscriptionStatus || userDoc.data().planType)) {
          console.log('Found subscription in users collection with userId', userDoc.data());
          // Create a subscription object from user data
          setSubscription({
            userId: userId,
            email: user.email,
            planType: userDoc.data().planType || 'free',
            status: userDoc.data().subscriptionStatus || 'unknown',
            expiryDate: userDoc.data().expiryDate,
            createdAt: userDoc.data().createdAt,
            customerId: userDoc.data().customerId,
            amount: userDoc.data().amount || 0,
            currency: userDoc.data().currency || 'usd'
          });
          setLoading(false);
          return;
        }
        
        // Third try: Check users collection using email
        const userByEmailRef = doc(db, 'users', user.email);
        const userByEmailDoc = await getDoc(userByEmailRef);
        
        if (userByEmailDoc.exists() && (userByEmailDoc.data().subscriptionStatus || userByEmailDoc.data().planType)) {
          console.log('Found subscription in users collection with email', userByEmailDoc.data());
          // Create a subscription object from user data
          setSubscription({
            userId: userId,
            email: user.email,
            planType: userByEmailDoc.data().planType || 'free',
            status: userByEmailDoc.data().subscriptionStatus || 'unknown',
            expiryDate: userByEmailDoc.data().expiryDate,
            createdAt: userByEmailDoc.data().createdAt,
            customerId: userByEmailDoc.data().customerId,
            amount: userByEmailDoc.data().amount || 0,
            currency: userByEmailDoc.data().currency || 'usd'
          });
          setLoading(false);
          return;
        }
        
        // Fourth try: Query purchases collection by email
        if (user.email) {
          const purchasesRef = collection(db, 'purchases');
          const q = query(purchasesRef, where('email', '==', user.email), limit(1));
          const snapshot = await getDocs(q);
          
          if (!snapshot.empty) {
            console.log('Found subscription by email query', snapshot.docs[0].data());
            setSubscription(snapshot.docs[0].data());
            setLoading(false);
            return;
          }
        }
        
        // If we get here, no subscription was found
        setSubscription({status: 'inactive'});
      } catch (err) {
        console.error('Error fetching subscription:', err);
        setError('Could not load subscription details');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user]);
  
  const formatCurrency = (amount, currency = 'usd') => {
    if (!amount) return 'N/A';
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
        minimumFractionDigits: 2
      }).format(amount / 100);
    } catch (e) {
      return `${amount / 100} ${currency.toUpperCase()}`;
    }
  };
  
  const getPlanLabel = (planType) => {
    switch (planType) {
      case 'daily':
        return 'Daily Plan';
      case 'sevenDays':
        return '7 Day Plan';
      case 'oneMonth':
        return '1 Month Plan';
      case 'threeMonth':
        return '3 Month Plan';
      case 'sixMonth':
        return '6 Month Plan';
      case 'yearly':
        return 'Annual Plan';
      case 'lifetime':
        return 'Lifetime Plan';
      default:
        return planType || 'Unknown Plan';
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <LoadingSpinner displayType='inline'/>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-md">
        <p>{error}</p>
      </div>
    );
  }

  if (!subscription || subscription.status === "inactive") {
    return (
      <div className="p-6 py-8 bg-gray-50 rounded-lg shadow-sm mb-5">
        <div className="flex flex-col items-center text-center">
          <svg 
            className="w-12 h-12 text-gray-400 mb-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No active plans</h3>
          <p className="text-gray-600 mb-4">Subscribe to access all premium features</p> 
          <Link to="/plans" className="inline-flex items-center px-4 py-2 pr-5 border border-transparent rounded-md shadow-sm 
            text-sm font-medium text-white btn-theme focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            <BiCrown className="mr-3 text-lg" /> Upgrade Now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="bg-white rounded-lg mb-3">
        {/* Status pill - displayed at the top */}
        <div className="flex justify-center mb-4">
          <span className={`inline-block px-3 py-1 text-sm rounded-full ${
            subscription.status === 'active' ? 'bg-green-100 text-green-800' : 
            subscription.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
            'bg-gray-100 text-gray-800'
          }`}>  
            {subscription.status ? 
              subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1) : 
              'Unknown'} 
          </span>
        </div>
        
        <div className="grid grid-cols-3 gap-2">
          {/* Column 1 */}
          <div className="flex flex-col items-center py-6 bg-gray-50 rounded-lg">
            <div className="text-gray-600 text-sm mb-2">Plan</div>
            <div className="font-medium text-center">{getPlanLabel(subscription.planType)}</div>
          </div>
          
          {/* Column 2 */}
          <div className="flex flex-col items-center py-6 bg-gray-50 rounded-lg">
            <div className="text-gray-600 text-sm mb-2">Currency</div>
            <div className="font-medium text-center">{subscription.currency?.toUpperCase() || 'N/A'}</div>
          </div>
          
          {/* Column 3 */}
          <div className="flex flex-col items-center py-6 bg-gray-50 rounded-lg">
            <div className="text-gray-600 text-sm mb-2">Amount</div>
            <div className="font-medium text-center">{formatCurrency(subscription.amount, subscription.currency)}</div>
          </div>
          
          {/* Second Row */}
          {/* Column 1 */}
          <div className="flex flex-col items-center py-6 bg-gray-50 rounded-lg">
            <div className="text-gray-600 text-sm mb-2">Expires</div>
            <div className="font-medium text-center">{formatDate(subscription.expiryDate)}</div>
          </div>
          
          {/* Column 2 */}
          <div className="flex flex-col items-center py-6 bg-gray-50 rounded-lg">
            <div className="text-gray-600 text-sm mb-2">Created</div>
            <div className="font-medium text-center">{formatDate(subscription.createdAt)}</div>
          </div>
          
          {/* Column 3 */}
          <div className="flex flex-col items-center py-6 bg-gray-50 rounded-lg">
            <div className="text-gray-600 text-sm mb-2">Last Updated</div>
            <div className="font-medium text-center">{formatDate(subscription.updatedAt || subscription.createdAt)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSubscription;